import type { CabinetEventView } from "@planerius/contracts/cabinet-events"
import { useNavigate } from "@tanstack/react-router"
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { closeEvent, fetchEvents, markEventsRead } from "../../api/events.api"
import { confirmAppointment, fetchAppointmentById } from "../../api/journal-actions.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem } from "../../api/problem"
import { newIdempotencyKey } from "../../api/idempotency"
import { cx } from "../../lib/cx"
import { setNavBadge } from "../../shell/nav-badges"
import { ProblemScreen } from "../../shell/states"
import { useOnline } from "../../shell/use-online"
import { useShellBranchSelection } from "../../shell/use-shell-branch"
import { Badge, Button, Chip, Chips, Counter, EmptyState, SkeletonText } from "../../ui"
import {
  EVENT_FILTERS,
  MASTER_FOOTER,
  type EventAction,
  type EventFilter,
  type EventRow,
  emptyWords,
  groupRows,
  rowTime,
  secondaryLabel,
  unreadInformational,
} from "./events-model"
import styles from "./events.module.css"

const PAGE = 30

/**
 * 1-Э1.9 «Центр уведомлений» — /notifications и /my/notifications.
 *
 * ОДИН ЭКРАН НА ДВЕ РОЛИ, И ЭТО НЕ ЭКОНОМИЯ. Лента администратора и лента мастера
 * различаются РОВНО тем, какие строки прислал сервер (адресность считается в выборке,
 * а не здесь) и куда ведут кнопки. Две копии экрана разошлись бы в первую же правку:
 * одну поправили, вторую забыли, и мастер остался бы с лентой, которая перестала
 * закрывать дела.
 *
 * ЧТО НА ЭКРАНЕ ГЛАВНОЕ. Не список событий, а СЧЁТЧИК ДЕЛ и кнопка, которая дело делает.
 * Поэтому «Требуют действия» — фильтр по умолчанию, и поэтому у строки «ждёт
 * подтверждения» стоит «Подтвердить», а не «Открыть запись»: дело закрывается здесь,
 * не уводя человека со экрана.
 *
 * СЧЁТЧИК ЕДЕТ В ОБОЛОЧКУ. Колокольчик в шапке и точка на пункте меню берут его отсюда
 * (setNavBadge): открытая лента — единственное место, где число известно точно, а не
 * «сколько было при загрузке кабинета».
 */
export function EventsScreen(props: {
  /** Лента мастера: другие адреса кнопок и честный подвал вместо обещания бота. */
  readonly master?: boolean
}): ReactNode {
  const master = props.master === true
  const online = useOnline()
  const navigate = useNavigate()
  const { context } = useCabinetAccess()

  /*
   * ФИЛИАЛ — ТОТ ЖЕ, ЧТО В ШАПКЕ И У КОЛОКОЛЬЧИКА (см. use-shell-branch.ts). Выбраны
   * «все филиалы» — лента сети целиком. Мастеру филиал не нужен: его лента адресована
   * ему лично, в каком бы филиале ни стояла запись.
   */
  const selection = useShellBranchSelection()
  const branchId = master || selection.kind !== "one" ? undefined : selection.branch.id
  const timeZone = selection.kind === "one" ? selection.branch.timezone : context.tenant.defaultTimezone
  const zoneOf = useCallback(
    (event: CabinetEventView): string =>
      context.branches.find((branch) => branch.id === event.branchId)?.timezone ?? timeZone,
    [context.branches, timeZone],
  )

  const [filterId, setFilterId] = useState<EventFilter["id"]>("action")
  const [items, setItems] = useState<readonly CabinetEventView[] | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [actionRequired, setActionRequired] = useState<number | null>(null)
  const [failure, setFailure] = useState<ApiProblem | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const filter = useMemo(() => EVENT_FILTERS.find((f) => f.id === filterId) ?? EVENT_FILTERS[0]!, [filterId])

  /*
   * СРЕЗ ЛЕНТЫ — ФИЛИАЛ И ЧИП. Ответ, запрошенный под другим срезом, в ленту не попадает:
   * при смене филиала «Показать ещё» и перечитывание после действия, начатые до смены,
   * приносили строки прежнего филиала и его число дел — и колокольчик под шапкой «Б»
   * показывал дела «А» (разбор w2but9x36, находка 22).
   */
  const sliceKey = `${branchId ?? "all"}|${filter.id}`
  const sliceRef = useRef(sliceKey)
  sliceRef.current = sliceKey

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const requested = `${branchId ?? "all"}|${filter.id}`
      try {
        const page = await fetchEvents(
          {
            onlyAction: filter.id === "action",
            ...(filter.kinds === undefined ? {} : { kinds: filter.kinds }),
            ...(branchId === undefined ? {} : { branchId }),
            limit: PAGE,
          },
          signal,
        )
        if (sliceRef.current !== requested) return
        setItems(page.items)
        setCursor(page.nextCursor)
        setActionRequired(page.actionRequired)
        setFailure(null)
        setProblem(null)
      } catch (error) {
        if (signal?.aborted === true || sliceRef.current !== requested) return
        if (!isApiProblem(error)) throw error
        setFailure(error)
      }
    },
    [filter, branchId],
  )

  useEffect(() => {
    // Новый срез — с чистого листа: строки и курсор прежнего не должны дожить до ответа.
    setItems(null)
    setCursor(null)
    const controller = new AbortController()
    void load(controller.signal)
    return () => {
      controller.abort()
    }
  }, [load])

  /*
   * СЧЁТЧИК В НАВИГАЦИЮ. Лента — единственное место, где число известно точно после
   * действия, поэтому она его и ставит. При уходе с ленты число НЕ стирается: срез у ленты
   * и колокольчика один (use-shell-branch.ts), а оболочка сама перечитывает его раз
   * в пять минут. Стёртое, оно оставляло колокольчик пустым до пяти минут при открытом
   * деле, и человек решал, что дел нет (разбор w2but9x36, находка 23).
   */
  useEffect(() => {
    if (actionRequired === null) return
    setNavBadge("notifications", actionRequired)
  }, [actionRequired])

  const rows = useMemo(
    () => (items === null ? [] : groupRows(items, new Date(), master, zoneOf)),
    [items, master, zoneOf],
  )
  const flat = useMemo(() => rows.flatMap((group) => group.rows), [rows])

  const runAction = async (action: EventAction, row: EventRow): Promise<void> => {
    if (action.kind === "open-journal") {
      // Журнал открывает день записи сам: в адресе идентификатор, панель раскрывается
      // по нему (1-Э1.14). Дату сюда подставлять нельзя — она в тексте события словом.
      await navigate({ to: "/journal", search: { appointment: action.appointmentId } })
      return
    }
    if (action.kind === "open-visit") {
      await navigate({
        to: "/my/visit/$appointmentId",
        params: { appointmentId: action.appointmentId },
        search: {},
      })
      return
    }
    if (action.kind === "open-leave") {
      await navigate({
        to: "/staff/$employeeId/leave",
        params: { employeeId: action.employeeId },
        search: { from: action.from, to: action.to },
      })
      return
    }
    if (action.kind === "open-week") {
      await navigate({ to: "/my/week", search: {} })
      return
    }
    setBusy(row.event.id)
    setProblem(null)
    try {
      /*
       * ПОДТВЕРЖДЕНИЕ ДЕЛАЕТСЯ ЗДЕСЬ, А НЕ ПЕРЕХОДОМ В ЖУРНАЛ: это и есть то дело,
       * которым строка закрывается, и уводить за ним человека со экрана незачем.
       *
       * ВЕРСИЮ ЗАПИСИ ПРИХОДИТСЯ ПРОЧИТАТЬ ПЕРЕД ДЕЙСТВИЕМ. Событие — снимок, оно
       * не знает нынешнего updatedAt, а сервер требует его на каждую правку записи:
       * рядом работают люди, и «подтвердить» запись, которую секунду назад перенесли,
       * значит подтвердить не то, что человек прочитал в ленте. Поэтому: читаем запись,
       * подтверждаем именно её версию, перечитываем ленту. Отказ по расхождению версий
       * человек увидит текстом, а обновлённая лента покажет, что на самом деле случилось.
       */
      const lookup = await fetchAppointmentById(action.appointmentId)
      await confirmAppointment(
        {
          appointmentId: action.appointmentId,
          expectedUpdatedAt: lookup.appointment.updatedAt,
        },
        newIdempotencyKey(`events-confirm:${action.appointmentId}`),
      )
      await load()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  const runSecondary = async (row: EventRow): Promise<void> => {
    setBusy(row.event.id)
    setProblem(null)
    try {
      if (row.needsWork) await closeEvent(row.event.id)
      else await markEventsRead([row.event.id])
      await load()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  const readAll = async (): Promise<void> => {
    setBusy("all")
    setProblem(null)
    try {
      // Пустой список — «все информационные» В СРЕЗЕ ЭТОЙ ЛЕНТЫ: филиал из шапки и чип.
      // Без среза кнопка над лентой одной точки гасила выделение у отмен и неявок
      // остальных, которых человек не видел. Дела этим не гасятся.
      await markEventsRead([], {
        ...(branchId === undefined ? {} : { branchId }),
        ...(filter.kinds === undefined ? {} : { kinds: filter.kinds }),
      })
      await load()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  const more = async (): Promise<void> => {
    if (cursor === null) return
    const requested = sliceKey
    setBusy("more")
    try {
      const page = await fetchEvents({
        onlyAction: filter.id === "action",
        ...(filter.kinds === undefined ? {} : { kinds: filter.kinds }),
        ...(branchId === undefined ? {} : { branchId }),
        cursor,
        limit: PAGE,
      })
      if (sliceRef.current !== requested) return
      setItems((prev) => [...(prev ?? []), ...page.items])
      setCursor(page.nextCursor)
      setActionRequired(page.actionRequired)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  if (failure !== null) {
    return <ProblemScreen problem={failure} onRetry={() => void load()} />
  }

  const unread = unreadInformational(flat)

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        {/*
          * ЗАГОЛОВОК — ТОЛЬКО У МАСТЕРА. У администратора название раздела уже стоит
          * в шапке оболочки, и второй крупный «Уведомления» прямо под ней читался как
          * сбой вёрстки (на телефоне — два одинаковых заголовка подряд). У мастера своей
          * шапки нет (MasterFrame), и заголовок из макета 1-Э1.9 нужен здесь. Для чтения
          * с экрана заголовок остаётся в обоих случаях: страница без h1 — страница
          * без названия.
          */}
        <h1 className={cx(styles.title, master ? undefined : "plnr-visually-hidden")}>
          Уведомления
        </h1>
        {actionRequired === null ? null : actionRequired === 0 ? (
          <Badge tone="gray">Дел нет</Badge>
        ) : (
          <Badge tone="error">Требуют действия: {actionRequired}</Badge>
        )}
        <div className={styles.headActions}>
          <Button
            variant="secondary"
            size="sm"
            disabled={!online || unread.length === 0 || busy !== null}
            {...(online ? {} : { deniedReason: "Без связи отметки не сохранятся" })}
            onClick={() => void readAll()}
          >
            Прочитать все информационные
          </Button>
        </div>
      </div>

      <Chips label="Фильтр событий" className={styles.filters}>
        {EVENT_FILTERS.map((item) => (
          <Chip
            key={item.id}
            active={item.id === filterId}
            {...(item.counted && item.id === "action" && actionRequired !== null
              ? { count: actionRequired, countLabel: "требуют действия" }
              : {})}
            onClick={() => {
              setFilterId(item.id)
            }}
          >
            {item.label}
          </Chip>
        ))}
      </Chips>

      {problem === null ? null : (
        <p className={styles.error} role="alert">
          {problem}
        </p>
      )}

      {items === null ? (
        <div className={styles.list}>
          <SkeletonText widths={["30%", "80%"]} />
          <SkeletonText widths={["24%", "70%"]} />
          <SkeletonText widths={["28%", "76%"]} />
        </div>
      ) : flat.length === 0 ? (
        <EmptyState title={emptyWords(filter).title} text={emptyWords(filter).text} />
      ) : (
        <div className={styles.list}>
          {rows.map((group) => (
            <section key={group.key} className={styles.group}>
              <h2 className={styles.groupTitle}>{group.title}</h2>
              {group.rows.map((row) => (
                <article
                  key={row.event.id}
                  className={cx(
                    styles.row,
                    row.needsWork ? styles.rowWork : undefined,
                    row.event.staleNote === null ? undefined : styles.rowStale,
                    row.event.read ? styles.rowRead : undefined,
                  )}
                >
                  {/* Точка: квадратная у дела, круглая у факта. Цвет не единственный
                      признак — форма различает их и в монохроме, и для дальтоника. */}
                  <span
                    className={cx(styles.dot, row.needsWork ? styles.dotWork : styles.dotInfo)}
                    aria-hidden="true"
                  />
                  <div className={styles.body}>
                    <div className={styles.meta}>
                      <span className={cx(styles.type, row.needsWork ? styles.typeWork : undefined)}>
                        {row.typeLabel}
                      </span>
                      <span className={styles.time}>· {rowTime(row.event.createdAt, zoneOf(row.event))}</span>
                      {row.event.staleNote === null ? null : (
                        <span className={styles.stale}>· {row.event.staleNote}</span>
                      )}
                      {row.event.closed && row.event.staleNote === null ? (
                        <span className={styles.stale}>· разобрано</span>
                      ) : null}
                    </div>
                    <p className={styles.text}>{row.event.body}</p>
                  </div>
                  <div className={styles.rowActions}>
                    {row.work === null ? null : (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={busy !== null || (!online && row.work.kind === "confirm")}
                        onClick={() => void runAction(row.work!, row)}
                      >
                        {row.work.label}
                      </Button>
                    )}
                    {row.link === null ? null : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy !== null}
                        onClick={() => void runAction(row.link!, row)}
                      >
                        {row.link.label}
                      </Button>
                    )}
                    {row.event.read && !row.needsWork ? null : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!online || busy !== null}
                        {...(online ? {} : { deniedReason: "Без связи отметки не сохранятся" })}
                        onClick={() => void runSecondary(row)}
                      >
                        {secondaryLabel(row)}
                      </Button>
                    )}
                  </div>
                </article>
              ))}
            </section>
          ))}
          {cursor === null ? null : (
            <Button
              variant="secondary"
              size="sm"
              className={styles.more}
              disabled={busy !== null}
              onClick={() => void more()}
            >
              Показать ещё
            </Button>
          )}
        </div>
      )}

      {master ? <p className={styles.footer}>{MASTER_FOOTER}</p> : null}
      {online ? null : (
        <p className={styles.offline} role="status">
          Нет связи: лента показана на момент последней загрузки, отметки заблокированы.
        </p>
      )}
    </div>
  )
}

/** Счётчик дел для шапки: колокольчик берёт его из ленты, когда та открыта. */
export function EventsCounter(props: { readonly value: number }): ReactNode {
  return <Counter value={props.value} label="событий требуют действия" tone="attention" />
}
